import { TabNames } from './../../const/check';
import { ReviewResult, ReviewCharacteristic } from 'types/check';
import { DocumentCategory, Review } from 'types/review-category';
import { AssessmentPagePayload } from 'types/assessment-payload';

import {
  CHECK_CATEGORY_CHANGE,
  CHECK_SELECTION,
  FETCH_ASSESSMENT_PAGE_ERROR,
  FETCH_ASSESSMENT_PAGE_START,
  FETCH_ASSESSMENT_PAGE_SUCCESS,
  GET_ASSESSMENT_PAGE,
  PAGE_CHANGE,
  SCALE_CHANGE,
  SET_EXTERNAL_ID,
  SET_PREV_EXTERNAL_ID,
  SET_QUALITY_CHECK_TABNAME,
  TOGGLE_BORDERS_VISIBILITY,
  SET_UNIT_BOX_COORDINATES,
  ADD_SSI_UNIT,
  REMOVE_SSI_UNIT,
  UPDATE_SSI_UNIT_RESULT,
  UPDATE_SSI_UNIT_TEXT_USED,
  UPDATE_SSI_UNIT_TEXT_REQUIRED,
} from './types';
import { BorderBox, AggregatedAssessmentPage } from 'types/check-results';

export const ERROR_MESSAGE =
  'Error happened while loading, try reloading the page';

interface IFetchAssessmentPageAction {
  type: typeof GET_ASSESSMENT_PAGE;
  pageNumber: number;
  token: string;
}

export interface IFetchAssessmentPageStart {
  type: typeof FETCH_ASSESSMENT_PAGE_START;
}

export interface IFetchAssessmentPageSuccess {
  type: typeof FETCH_ASSESSMENT_PAGE_SUCCESS;
  currentPage?: AssessmentPagePayload;
  cachedPage?: AggregatedAssessmentPage;
}

export interface IFetchAssessmentPageError {
  type: typeof FETCH_ASSESSMENT_PAGE_ERROR;
  error: {
    message: string;
    statusCode: number;
  };
}

export interface IPageChange {
  type: typeof PAGE_CHANGE;
  pageNumber: number;
}

export interface IScaleChange {
  type: typeof SCALE_CHANGE;
  scale: number;
}

export interface ICheckCategoryChange {
  type: typeof CHECK_CATEGORY_CHANGE;
  category: Review | DocumentCategory;
  result: ReviewResult | null;
}

export interface ICheckSelection {
  type: typeof CHECK_SELECTION;
  id: string | null;
}

export interface IExternalId {
  type: typeof SET_EXTERNAL_ID | typeof SET_PREV_EXTERNAL_ID;
  id: string | null;
}

export interface IToggleBordersVisibility {
  type: typeof TOGGLE_BORDERS_VISIBILITY;
}

export interface IChangeTabName {
  type: typeof SET_QUALITY_CHECK_TABNAME;
  tabTitle: TabNames;
}

export function pageChangeAction(pageNumber: number): IPageChange {
  return {
    type: PAGE_CHANGE,
    pageNumber,
  };
}

export function zoomAction(scale: number): IScaleChange {
  return {
    type: SCALE_CHANGE,
    scale,
  };
}

export function checkCategoryChangeAction(
  category: Review | DocumentCategory,
  result: ReviewResult | null
): ICheckCategoryChange {
  return {
    type: CHECK_CATEGORY_CHANGE,
    category,
    result,
  };
}

export function setTabNameTitle(tabTitle: TabNames): IChangeTabName {
  return {
    type: SET_QUALITY_CHECK_TABNAME,
    tabTitle,
  };
}

export function checkSelectionAction(id: string | null): ICheckSelection {
  return {
    type: CHECK_SELECTION,
    id,
  };
}

export function setExternalIdAction(id: string | null): IExternalId {
  return {
    type: SET_EXTERNAL_ID,
    id,
  };
}

export function setPrevExternalIdAction(id: string | null): IExternalId {
  return {
    type: SET_PREV_EXTERNAL_ID,
    id,
  };
}

export function toggleBordersVisibility() {
  return {
    type: TOGGLE_BORDERS_VISIBILITY,
  };
}

export function fetchAssessmentPageAction(
  pageNumber: number,
  token: string
): IFetchAssessmentPageAction {
  return {
    type: GET_ASSESSMENT_PAGE,
    pageNumber,
    token,
  };
}

export function fetchAssessmentPageStartAction(): IFetchAssessmentPageStart {
  return {
    type: FETCH_ASSESSMENT_PAGE_START,
  };
}

export function fetchAssessmentPageSuccessAction(
  currentPage?: AssessmentPagePayload,
  cachedPage?: AggregatedAssessmentPage
): IFetchAssessmentPageSuccess {
  return {
    type: FETCH_ASSESSMENT_PAGE_SUCCESS,
    currentPage,
    cachedPage,
  };
}

export function fetchAssessmentPageErrorAction(
  message = ERROR_MESSAGE,
  statusCode: number
): IFetchAssessmentPageError {
  return {
    type: FETCH_ASSESSMENT_PAGE_ERROR,
    error: {
      message,
      statusCode,
    },
  };
}

export interface ISetUnitBoxCoordinates {
  type: typeof SET_UNIT_BOX_COORDINATES;
  externalId: string;
  newCoordinates: BorderBox;
}

export function setUnitBoxCoordinatesAction(
  externalId: string,
  newCoordinates: BorderBox
): ISetUnitBoxCoordinates {
  return {
    type: SET_UNIT_BOX_COORDINATES,
    externalId,
    newCoordinates,
  };
}

export interface IAddSSIUnit {
  type: typeof ADD_SSI_UNIT;
  newCoordinates: BorderBox;
}

export function addSSIUnitAction(newCoordinates: BorderBox): IAddSSIUnit {
  return {
    type: ADD_SSI_UNIT,
    newCoordinates,
  };
}

export interface IRemoveSSIUnit {
  type: typeof REMOVE_SSI_UNIT;
  externalId: string;
}

export function removeSSIUnitAction(externalId: string): IRemoveSSIUnit {
  return {
    type: REMOVE_SSI_UNIT,
    externalId,
  };
}

export interface IUpdateSSIUnitResult {
  type: typeof UPDATE_SSI_UNIT_RESULT;
  characteristic: ReviewCharacteristic;
  externalId: string;
}

export function updateSSIUnitResult(
  externalId: string,
  characteristic: ReviewCharacteristic
): IUpdateSSIUnitResult {
  return {
    type: UPDATE_SSI_UNIT_RESULT,
    externalId,
    characteristic,
  };
}

export interface IUpdateSSIUnitTextUsed {
  type: typeof UPDATE_SSI_UNIT_TEXT_USED;
  externalId: string;
  text: string;
}

export function updateSSIUnitTextUsed(
  externalId: string,
  text: string
): IUpdateSSIUnitTextUsed {
  return {
    type: UPDATE_SSI_UNIT_TEXT_USED,
    externalId,
    text,
  };
}

export interface IUpdateSSIUnitTextRequired {
  type: typeof UPDATE_SSI_UNIT_TEXT_REQUIRED;
  externalId: string;
  text: string;
}

export function updateSSIUnitTextRequired(
  externalId: string,
  text: string
): IUpdateSSIUnitTextRequired {
  return {
    type: UPDATE_SSI_UNIT_TEXT_REQUIRED,
    externalId,
    text,
  };
}
